import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNoizaMetrics, DateRange } from '../../lib/noiza-metrics-hooks';
import { formatCurrency } from '../../lib/utils';
import { TrendingUpIcon, ShoppingCartIcon, ReceiptIcon, StoreIcon, UsersIcon, UserCheckIcon, ArrowLeftIcon, ActivityIcon, RefreshCwIcon, Loader2Icon, CalendarIcon, FilterIcon, ClockIcon, AwardIcon, AlertTriangleIcon, ChevronDownIcon, FileTextIcon } from 'lucide-react';
// ─── Helpers ───────────────────────────────────────────────
// All date helpers return Costa Rica local date (UTC-6).
// Using UTC `.toISOString()` would give the wrong day for users in CR
// late at night (it'd flip to tomorrow's date).
function getCRDate(d: Date = new Date()): Date {
  // Convert to CR time by adjusting the timestamp by the difference
  // between UTC and CR offsets.
  const utcMs = d.getTime() + d.getTimezoneOffset() * 60000;
  return new Date(utcMs - 6 * 60 * 60000);
}
function formatYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function formatCompactCurrency(value: number): string {
  if (value >= 1000000) return `₡${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `₡${(value / 1000).toFixed(0)}k`;
  return formatCurrency(value);
}
function formatDayLabel(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${d} ${months[m - 1]}`;
  } catch {
    return dateStr;
  }
}
// El hook ya entrega el canal clasificado por `orders.source`
// (Marketplace, QR, Mesero, Caja, Sin clasificar). No se reinterpreta.
function normalizeChannel(ch: string): string {
  return ch || 'Sin clasificar';
}
const CHANNEL_COLORS: Record<string, string> = {
  QR: '#10b981',
  Mesero: '#6366f1',
  Marketplace: '#f59e0b',
  Caja: '#8b5cf6',
  'Sin clasificar': '#6b7280'
};
function getDaysAgo(days: number): string {
  const d = getCRDate();
  d.setDate(d.getDate() - days);
  return formatYMD(d);
}
function getToday(): string {
  return formatYMD(getCRDate());
}
function getFirstDayOfMonth(): string {
  const d = getCRDate();
  d.setDate(1);
  return formatYMD(d);
}
// ─── Dark Tooltip ──────────────────────────────────────────
function DarkTooltip({
  active,
  payload,
  label,
  isCurrency
}: any) {
  if (!active || !payload?.length) return null;
  return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-4 py-3 rounded-xl shadow-2xl text-sm z-50" data-id="element-5805" data-mp="2i54fyglvad18">
      <p className="font-medium text-white/60 mb-1.5 text-xs uppercase tracking-wider" data-id="element-5806" data-mp="2n3fqktjzkrki">
        {label}
      </p>
      {payload.map((entry: any, i: number) => <p key={i} className="flex items-center gap-2" data-id="element-5807" data-mp="2amn1rx31qa1x" data-mpi={i}>
          <span className="w-2 h-2 rounded-full" style={{
        backgroundColor: entry.color || entry.fill
      }} data-id="element-5808" data-mp="2ompj9i7fz1gn" />
          <span className="text-white/50" data-id="element-5809" data-mp="2jlrx6ujb9n9v">{entry.name}:</span>
          <span className="font-semibold text-white" data-id="element-5810" data-mp="1vz1zg5dzwev2">
            {isCurrency ? formatCurrency(Number(entry.value ?? 0)) : Number(entry.value ?? 0).toLocaleString()}
          </span>
        </p>)}
    </div>;
}
// ─── KPI Card ──────────────────────────────────────────────
interface KpiCardProps {
  label: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  accentColor: string;
}
function KpiCard({
  label,
  value,
  subtitle,
  icon,
  accentColor
}: KpiCardProps) {
  return <div className="relative overflow-hidden rounded-2xl bg-[#12121a] border border-white/[0.06] p-5 group" data-id="element-5811" data-mp="188fs6gbteym4">
      <div className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-[0.07] -translate-y-8 translate-x-8`} style={{
      backgroundColor: accentColor
    }} data-id="element-5812" data-mp="bf7zjsznbrxe" />
      <div className="relative" data-id="element-5813" data-mp="kg61m5qixpdc">
        <div className="flex items-center justify-between mb-4" data-id="element-5814" data-mp="p8z8az5jz7s2">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
          backgroundColor: `${accentColor}15`
        }} data-id="element-5815" data-mp="pak5yxn5z6u5">
            {icon}
          </div>
        </div>
        <p className="text-[13px] font-medium text-white/40 uppercase tracking-wider" data-id="element-5816" data-mp="145q7b7bbehh5">
          {label}
        </p>
        <p className="text-3xl font-bold text-white mt-1 tracking-tight" data-id="element-5817" data-mp="186a94o6u299m">
          {value}
        </p>
        {subtitle && <p className="text-xs text-white/30 mt-1" data-id="element-5818" data-mp="35z5zw9qyq6er">{subtitle}</p>}
      </div>
    </div>;
}
// ─── Main Page ─────────────────────────────────────────────
export function NoizaMetricsPage() {
  const [restaurantId, setRestaurantId] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: getDaysAgo(30),
    to: getToday()
  });
  const [datePreset, setDatePreset] = useState<string>('30d');
  const {
    data,
    loading,
    error,
    refetch,
    restaurants
  } = useNoizaMetrics(restaurantId, dateRange);
  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    const to = getToday();
    let from = to;
    if (preset === 'all') {
      // Sin límites de fecha: todo el historial disponible.
      setDateRange({
        from: '',
        to: ''
      });
      return;
    }
    if (preset === 'today') from = to;else if (preset === '7d') from = getDaysAgo(7);else if (preset === '30d') from = getDaysAgo(30);else if (preset === 'month') from = getFirstDayOfMonth();
    setDateRange({
      from,
      to
    });
  };
  // Derived Data for Charts
  const dailyChartData = useMemo(() => {
    const source = data.salesDaily.length > 0 ? data.salesDaily : data.gmvDaily;
    // Aggregate by day if multiple restaurants
    const aggregated = source.reduce((acc: any, curr: any) => {
      const day = curr.day;
      if (!acc[day]) acc[day] = {
        day,
        label: formatDayLabel(day),
        gmv: 0,
        orders: 0
      };
      acc[day].gmv += curr.gmv;
      acc[day].orders += curr.orders;
      return acc;
    }, {});
    return Object.values(aggregated).sort((a: any, b: any) => a.day.localeCompare(b.day));
  }, [data.salesDaily, data.gmvDaily]);
  const hourlyChartData = useMemo(() => {
    const aggregated = data.salesHourly.reduce((acc: any, curr: any) => {
      const hour = curr.hour;
      if (!acc[hour]) acc[hour] = {
        hour,
        label: `${hour}:00`,
        gmv: 0,
        orders: 0
      };
      acc[hour].gmv += curr.gmv;
      acc[hour].orders += curr.orders;
      return acc;
    }, {});
    return Object.values(aggregated).sort((a: any, b: any) => Number(a.hour) - Number(b.hour));
  }, [data.salesHourly]);
  const channelChartData = useMemo(() => {
    return data.channelDistribution.map(d => ({
      name: normalizeChannel(d.channel),
      value: d.orders,
      pct: d.pct,
      color: CHANNEL_COLORS[normalizeChannel(d.channel)] || '#6b7280'
    })).filter(d => d.value > 0);
  }, [data.channelDistribution]);
  const totalChannelOrders = channelChartData.reduce((sum, d) => sum + d.value, 0);
  const topRestaurantsChartData = useMemo(() => {
    return data.gmvByRestaurant.slice(0, 5).map(r => ({
      name: String(r.restaurant_name || '').length > 15 ? String(r.restaurant_name || '').substring(0, 15) + '...' : String(r.restaurant_name || ''),
      fullName: r.restaurant_name,
      gmv: r.gmv
    }));
  }, [data.gmvByRestaurant]);
  const topProductsChartData = useMemo(() => {
    return data.topProducts.slice(0, 5).map(p => ({
      name: String(p.product_name || '').length > 15 ? String(p.product_name || '').substring(0, 15) + '...' : String(p.product_name || ''),
      fullName: p.product_name,
      revenue: p.revenue,
      quantity: p.quantity
    }));
  }, [data.topProducts]);
  // Insights
  const insights = useMemo(() => {
    let bestDay = {
      day: '-',
      val: 0
    };
    let worstDay = {
      day: '-',
      val: Infinity
    };
    dailyChartData.forEach((d: any) => {
      if (d.gmv > bestDay.val) bestDay = {
        day: d.label,
        val: d.gmv
      };
      if (d.gmv < worstDay.val && d.gmv > 0) worstDay = {
        day: d.label,
        val: d.gmv
      };
    });
    let bestHour = {
      hour: '-',
      val: 0
    };
    let worstHour = {
      hour: '-',
      val: Infinity
    };
    hourlyChartData.forEach((d: any) => {
      if (d.gmv > bestHour.val) bestHour = {
        hour: d.label,
        val: d.gmv
      };
      if (d.gmv < worstHour.val && d.gmv > 0) worstHour = {
        hour: d.label,
        val: d.gmv
      };
    });
    let bestRest = data.gmvByRestaurant[0];
    let worstRest = data.gmvByRestaurant[data.gmvByRestaurant.length - 1];
    return {
      bestDay,
      worstDay,
      bestHour,
      worstHour,
      bestRest,
      worstRest
    };
  }, [dailyChartData, hourlyChartData, data.gmvByRestaurant]);
  return <div className="min-h-screen bg-[#0a0a0f] text-white pb-20" data-id="element-5819" data-mp="xbhyqmwkohvf">
      {/* ─── Header ─────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/[0.06]" data-id="element-5820" data-mp="njc7jfe5a4ha">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-5821" data-mp="il0yv7zl600n">
          <div className="flex items-center gap-4" data-id="element-5822" data-mp="ehm6dhugnr6b">
            <Link to="/admin/enrollment" className="w-9 h-9 rounded-xl bg-white/[0.06] flex items-center justify-center hover:bg-white/[0.1] transition-colors" data-id="element-5823" data-mp="9jue2mpwm31l">
              <ArrowLeftIcon className="w-4 h-4 text-white/60" data-id="element-5824" data-mp="13o9r75hpdog7" />
            </Link>
            <div data-id="element-5825" data-mp="13mfci8x0yskk">
              <div className="flex items-center gap-2.5" data-id="element-5826" data-mp="1icwf3vleboj1">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20" data-id="element-5827" data-mp="1ice8c513xrwv">
                  <ActivityIcon className="w-4 h-4 text-white" data-id="element-5828" data-mp="pnp78601xml4" />
                </div>
                <h1 className="text-lg font-bold tracking-tight" data-id="element-5829" data-mp="kpxc73441kcp">NOIZA</h1>
                <span className="text-xs font-medium text-white/30 bg-white/[0.06] px-2 py-0.5 rounded-md" data-id="element-5830" data-mp="zmof3mczte72">
                  Executive Dashboard
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-5831" data-mp="2b1bq871epmp3">
            <button onClick={refetch} disabled={loading} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] transition-colors text-sm text-white/60 hover:text-white/80 disabled:opacity-50" data-id="element-5832" data-mp="2b00dhhpzh4lu">
              {loading ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-5833" data-mp="2iizuxhtvwm2k" /> : <RefreshCwIcon className="w-4 h-4" data-id="element-5834" data-mp="24neazp2icsh0" />}
              <span className="hidden sm:inline" data-id="element-5835" data-mp="28o7zajjv49af">Actualizar</span>
            </button>
          </div>
        </div>
      </header>

      {/* ─── Filters Bar ────────────────────────────────── */}
      <div className="bg-[#12121a] border-b border-white/[0.06]" data-id="element-5836" data-mp="31asll5lhdv74">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-id="element-5837" data-mp="2nkp6cyl0r1r6">
          <div className="flex items-center gap-3" data-id="element-5838" data-mp="2rl96mb0w2lhj">
            <FilterIcon className="w-4 h-4 text-white/40" data-id="element-5839" data-mp="2rjo7s97jkgdv" />
            <div className="relative" data-id="element-5840" data-mp="3fatosrp7xepr">
              <select value={restaurantId} onChange={e => setRestaurantId(e.target.value)} className="appearance-none bg-white/[0.06] border border-white/[0.1] rounded-lg pl-3 pr-8 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors" data-id="element-5841" data-mp="3f9s9gb166628">
                <option value="all" data-id="element-5842" data-mp="3kwugwhol0ps8">Todos los restaurantes</option>
                {restaurants.map(r => <option key={r.id} value={r.id} data-id="element-5843" data-mp="3fxhmnbvdsgfm">
                    {r.name}
                  </option>)}
              </select>
              <ChevronDownIcon className="w-4 h-4 text-white/40 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-5844" data-mp="3b1aq29ovkhac" />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap pb-1 sm:pb-0" data-id="element-5845" data-mp="327xko89mjn7d">
            <CalendarIcon className="w-4 h-4 text-white/40 mr-1" data-id="element-5846" data-mp="2x8uj4xsurz45" />
            {[{
            id: 'all',
            label: 'Todo'
          }, {
            id: 'today',
            label: 'Hoy'
          }, {
            id: '7d',
            label: '7 días'
          }, {
            id: '30d',
            label: '30 días'
          }, {
            id: 'month',
            label: 'Mes actual'
          }].map(preset => <button key={preset.id} onClick={() => handleDatePreset(preset.id)} className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${datePreset === preset.id ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/[0.03] text-white/60 border border-white/[0.05] hover:bg-white/[0.08]'}`} data-id="element-5847" data-mp="2s3qzvgw1r3g4">
                {preset.label}
              </button>)}

            <div className="h-5 w-px bg-white/10 mx-1" data-id="element-5848" data-mp="2966am8pde2uz" />

            <div className="flex items-center gap-2" data-id="element-5849" data-mp="1bo684o29u8yp">
              <label className="text-xs text-white/40" data-id="element-5850" data-mp="1go2c4nayy0e9">Desde</label>
              <input type="date" value={dateRange.from} onChange={e => {
              setDatePreset('custom');
              setDateRange(prev => ({
                ...prev,
                from: e.target.value
              }));
            }} className="bg-white/[0.06] border border-white/[0.1] rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors [color-scheme:dark]" data-id="element-5851" data-mp="1gm7sdcbfoebr" />
              <label className="text-xs text-white/40" data-id="element-5852" data-mp="wq9nvedjagc">Hasta</label>
              <input type="date" value={dateRange.to} onChange={e => {
              setDatePreset('custom');
              setDateRange(prev => ({
                ...prev,
                to: e.target.value
              }));
            }} className="bg-white/[0.06] border border-white/[0.1] rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors [color-scheme:dark]" data-id="element-5853" data-mp="wgn6hsjflfe" />
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-id="element-5854" data-mp="w5afitx76hdq">
        {error && <div className="rounded-2xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400 flex items-center gap-3" data-id="element-5855" data-mp="hg5t10jddqzt">
            <AlertTriangleIcon className="w-5 h-5" data-id="element-5856" data-mp="mdxq08yvg3av" />
            {error}
          </div>}

        {error && !loading ? null : loading ? <div className="space-y-8" data-id="element-5857" data-mp="dflxu2icwxgb">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-5858" data-mp="dgncsi2hu43b">
              {Array.from({
            length: 6
          }).map((_, i) => <div key={i} className="h-32 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-5859" data-mp="3hxr0e7h7zhs" data-mpi={i} />)}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-5860" data-mp="1xvp79wkb9hm7">
              <div className="lg:col-span-2 h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-5861" data-mp="1xst8y74jwqx8" />
              <div className="h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-5862" data-mp="1wbnna9aajw5l" />
            </div>
          </div> : <>
            {/* ─── KPI Cards ──────────────────────────── */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-5863" data-mp="1mhnik8ix8sxe">
              <KpiCard label="GMV Total" value={formatCompactCurrency(Number(data.gmv.total_gmv ?? 0))} subtitle="Ingresos brutos" icon={<TrendingUpIcon className="w-5 h-5 text-emerald-400" data-id="element-5865" data-mp="2a66tywx9pltp" />} accentColor="#10b981" data-id="element-5864" data-mp="1igbtq57vgbq9" />
              <KpiCard label="Órdenes" value={Number(data.gmv.total_orders ?? 0).toLocaleString()} subtitle="Total procesadas" icon={<ShoppingCartIcon className="w-5 h-5 text-blue-400" data-id="element-5867" data-mp="2xwao8dokg981" />} accentColor="#3b82f6" data-id="element-5866" data-mp="25cc1p53ljyj0" />
              <KpiCard label="Ticket Promedio" value={formatCurrency(Number(data.gmv.avg_ticket ?? 0))} subtitle="Por orden" icon={<ReceiptIcon className="w-5 h-5 text-amber-400" data-id="element-5869" data-mp="kmu1jdifuzbw" />} accentColor="#f59e0b" data-id="element-5868" data-mp="flmtregaq058" />
              <KpiCard label="Clientes Únicos" value={Number(data.uniqueCustomers ?? 0).toLocaleString()} subtitle="Compradores distintos" icon={<UsersIcon className="w-5 h-5 text-cyan-400" data-id="element-5871" data-mp="13jlru6weeepg" />} accentColor="#06b6d4" data-id="element-5870" data-mp="tleedy1bies7" />
              <KpiCard label="Recurrentes" value={Number(data.repeatCustomers.repeat_count ?? 0).toLocaleString()} subtitle={Number(data.repeatCustomers.repeat_pct ?? 0) > 0 ? `${Math.round(Number(data.repeatCustomers.repeat_pct ?? 0))}% del total` : 'Clientes que regresan'} icon={<UserCheckIcon className="w-5 h-5 text-pink-400" data-id="element-5873" data-mp="x5d0qp3rxx51" />} accentColor="#ec4899" data-id="element-5872" data-mp="18ak6z35nh6r4" />
              <KpiCard label="Autoservicio" value={`${Math.round(Number(data.selfServicePct.self_service_pct ?? 0))}%`} subtitle={`Mesero: ${Math.round(Number(data.selfServicePct.waiter_pct ?? 0))}% · Caja: ${Math.round(Number(data.selfServicePct.cashier_pct ?? 0))}%`} icon={<StoreIcon className="w-5 h-5 text-purple-400" data-id="element-5875" data-mp="9hlmvh2l5c1n" />} accentColor="#8b5cf6" data-id="element-5874" data-mp="s9xmvx5ro18m" />
            </div>

            {/* ─── Insights Section ───────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5876" data-mp="iia2uhbjhmh6">
              <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 p-4 flex items-start gap-4" data-id="element-5877" data-mp="ifxada85ntpv">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0" data-id="element-5878" data-mp="3mx1enzt6ptov">
                  <AwardIcon className="w-5 h-5 text-emerald-400" data-id="element-5879" data-mp="3crjgqi09uptu" />
                </div>
                <div data-id="element-5880" data-mp="3hk31q01agn8o">
                  <h3 className="text-sm font-semibold text-emerald-400" data-id="element-5881" data-mp="3hkcjx1g3dq3t">
                    Mejor Día
                  </h3>
                  <p className="text-white font-medium mt-1" data-id="element-5882" data-mp="32sar21cfvxda">
                    {insights.bestDay.day}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-5883" data-mp="3vi9l6b5hqkot">
                    {formatCurrency(insights.bestDay.val)}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 p-4 flex items-start gap-4" data-id="element-5884" data-mp="3lkvdp11jp68n">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0" data-id="element-5885" data-mp="3qi4zm6pq6zrq">
                  <ClockIcon className="w-5 h-5 text-blue-400" data-id="element-5886" data-mp="2a45375q39s98" />
                </div>
                <div data-id="element-5887" data-mp="2f6466ehx2n5n">
                  <h3 className="text-sm font-semibold text-blue-400" data-id="element-5888" data-mp="2f2gfro7rqgv5">
                    Hora Pico
                  </h3>
                  <p className="text-white font-medium mt-1" data-id="element-5889" data-mp="1jynujxz1v6yt">
                    {insights.bestHour.hour}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-5890" data-mp="15y3ayzlb9749">
                    {formatCurrency(insights.bestHour.val)}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 p-4 flex items-start gap-4" data-id="element-5891" data-mp="w8tl7qdxw0jb">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0" data-id="element-5892" data-mp="1124rnqeuafap">
                  <StoreIcon className="w-5 h-5 text-amber-400" data-id="element-5893" data-mp="n6tzwlvw6aak" />
                </div>
                <div data-id="element-5894" data-mp="r85tt3lsthok">
                  <h3 className="text-sm font-semibold text-amber-400" data-id="element-5895" data-mp="dahaw1bakkm5">
                    Top Restaurante
                  </h3>
                  <p className="text-white font-medium mt-1 truncate max-w-[150px]" data-id="element-5896" data-mp="i8ipa8xrvvra">
                    {insights.bestRest?.restaurant_name || '-'}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-5897" data-mp="3pubgqz43x56o">
                    {insights.bestRest ? formatCurrency(insights.bestRest.gmv) : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* ─── Total cobrado (payments.amount) ───── */}
            <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] overflow-hidden" data-id="element-5898" data-mp="hll3s7u1eyyw">
              <div className="p-5 border-b border-white/[0.06] flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2" data-id="element-5899" data-mp="cmi472wttkyb">
                <div data-id="element-5900" data-mp="reio8jilay05">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5901" data-mp="mdcsectmuume">
                    Total cobrado
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5902" data-mp="10ccjrs8j5nm9">
                    Suma de pagos registrados por fecha de pago · distinto del
                    GMV (por fecha de pedido)
                  </p>
                </div>
                <div className="sm:text-right" data-id="element-5903" data-mp="13gaadho7mg1p">
                  <p className="text-2xl font-bold text-white tracking-tight" data-id="element-5904" data-mp="18dk59cudbjwx">
                    {formatCurrency(Number(data.collected.total ?? 0))}
                  </p>
                  <p className="text-xs text-white/30" data-id="element-5905" data-mp="1datvgupkzzlt">
                    {Number(data.collected.payments ?? 0).toLocaleString()}{' '}
                    pagos
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-5906" data-mp="1r41lwj1xep1x">
                <table className="w-full text-sm" data-id="element-5907" data-mp="1r2qhqi4kjfaa">
                  <thead data-id="element-5908" data-mp="1w3o0avthqbcb">
                    <tr className="text-xs text-white/30 uppercase tracking-wider bg-white/[0.02]" data-id="element-5909" data-mp="1w4fuq3bcq310">
                      <th className="text-left px-5 py-3 font-medium" data-id="element-5910" data-mp="211fxa48zsnnd">
                        Restaurante
                      </th>
                      <th className="text-right px-5 py-3 font-medium" data-id="element-5911" data-mp="1rg99tkaielt">
                        Pagos
                      </th>
                      <th className="text-right px-5 py-3 font-medium" data-id="element-5912" data-mp="2jyhklhknkp0p">
                        Cobrado
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-5913" data-mp="2f66ic82fwr4z">
                    {data.collected.byRestaurant.map(r => <tr key={r.restaurant_id || 'none'} className="border-t border-white/[0.04]" data-id="element-5914" data-mp="2b4bmhj3a35yl">
                        <td className="px-5 py-3 text-white/90" data-id="element-5915" data-mp="32wakeazzcitv">
                          {r.restaurant_name}
                        </td>
                        <td className="px-5 py-3 text-right text-white/60" data-id="element-5916" data-mp="2twxbpkjojya7">
                          {r.payments.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right font-medium text-white" data-id="element-5917" data-mp="2oxaxp8w12e0c">
                          {formatCurrency(r.amount)}
                        </td>
                      </tr>)}
                    {data.collected.byRestaurant.length === 0 && <tr data-id="element-5918" data-mp="31ealysjjlgsr">
                        <td colSpan={3} className="px-5 py-8 text-center text-white/30" data-id="element-5919" data-mp="31e2b166zclk7">
                          Sin pagos en el periodo
                        </td>
                      </tr>}
                  </tbody>
                  {data.collected.byRestaurant.length > 0 && <tfoot data-id="element-5920" data-mp="3l4npg5lbdo2k">
                      <tr className="border-t border-white/[0.12] bg-white/[0.03]" data-id="element-5921" data-mp="3l18f1g2l537h">
                        <td className="px-5 py-3 font-semibold text-white" data-id="element-5922" data-mp="3p44yf8zy93ch">
                          Total general
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-white/80" data-id="element-5923" data-mp="3g3qby6b91i7e">
                          {Number(data.collected.payments).toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right font-bold text-emerald-400" data-id="element-5924" data-mp="24ot65kquiedc">
                          {formatCurrency(Number(data.collected.total))}
                        </td>
                      </tr>
                    </tfoot>}
                </table>
              </div>
            </div>

            {/* ─── Charts Row 1 ───────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-5925" data-mp="2175j03grk6b5">
              {/* GMV Daily Chart */}
              <div className="lg:col-span-2 rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5926" data-mp="1dhb83rg08rtj">
                <div className="flex items-center justify-between mb-6" data-id="element-5927" data-mp="19g97b66fw7pg">
                  <div data-id="element-5928" data-mp="19e65zpzu6s5f">
                    <h2 className="text-sm font-semibold text-white/80" data-id="element-5929" data-mp="1n74ac1u9bul4">
                      GMV Diario
                    </h2>
                    <p className="text-xs text-white/30 mt-0.5" data-id="element-5930" data-mp="1ifvxevtpxr37">
                      Ingresos brutos por día
                    </p>
                  </div>
                </div>
                {dailyChartData.length === 0 ? <div className="h-64 flex items-center justify-center text-white/20 text-sm" data-id="element-5931" data-mp="ppxg5mlrmy93">
                    Sin datos
                  </div> : <div className="h-64" data-id="element-5932" data-mp="2us1fvagqpkbi">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5933" data-mp="2uqzuac23lffj">
                      <AreaChart data={dailyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-5934" data-mp="2zkkri1vva5do">
                        <defs data-id="element-5935" data-mp="2buqi5uarayy6">
                          <linearGradient id="gmvGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-5936" data-mp="2gtl2x1saaxkx">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} data-id="element-5937" data-mp="3s86w9dgtzg24" />
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0} data-id="element-5938" data-mp="1kmgncmul18mi" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-5939" data-mp="16uikgtrgs8ys" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} interval="preserveStartEnd" data-id="element-5940" data-mp="wwm67t38cxwg" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-5941" data-mp="1r6pea8gp2qvj" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-5943" data-mp="18dtvk9g1h99h" />} data-id="element-5942" data-mp="18edffvp927q5" />
                        <Area type="monotone" dataKey="gmv" name="GMV" stroke="#10b981" strokeWidth={2} fill="url(#gmvGrad)" data-id="element-5944" data-mp="18h9btflg8och" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Channel Distribution */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5945" data-mp="2xrqmhsoqy9uy">
                <div className="mb-4" data-id="element-5946" data-mp="m6r66uejc61b">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5947" data-mp="h7mx03rm28az">
                    Canales
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5948" data-mp="vwj1xrydf57d">
                    Distribución de pedidos
                  </p>
                </div>
                {channelChartData.length === 0 ? <div className="h-48 flex items-center justify-center text-white/20 text-sm" data-id="element-5949" data-mp="14w63cdr3ob0f">
                    Sin datos
                  </div> : <>
                    <div className="h-48" data-id="element-5950" data-mp="1eveyh9xpbvod">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-5951" data-mp="1et2hayzrppcd">
                        <PieChart data-id="element-5952" data-mp="19xdemmsgbsbu">
                          <Pie data={channelChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value" strokeWidth={0} data-id="element-5953" data-mp="19u7od4836kej">
                            {channelChartData.map((entry, i) => <Cell key={i} fill={entry.color} data-id="element-5954" data-mp="10g5j9va5je95" data-mpi={i} />)}
                          </Pie>
                          <Tooltip content={({
                      active,
                      payload
                    }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      const pct = totalChannelOrders > 0 ? Math.round(d.value / totalChannelOrders * 100) : 0;
                      return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-3 py-2 rounded-xl shadow-2xl text-sm" data-id="element-5956" data-mp="27w1xo9y0i74l">
                                  <p className="font-medium" data-id="element-5957" data-mp="3urgj19t3ej86">{d.name}</p>
                                  <p className="text-white/50" data-id="element-5958" data-mp="3upnfw4houh4x">
                                    {Number(d.value ?? 0).toLocaleString()}{' '}
                                    pedidos ({pct}%)
                                  </p>
                                </div>;
                    }} data-id="element-5955" data-mp="weakoyjlvez2" />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2 mt-2" data-id="element-5959" data-mp="3ft6276m10qf7">
                      {channelChartData.map(entry => {
                  const pct = totalChannelOrders > 0 ? Math.round(entry.value / totalChannelOrders * 100) : 0;
                  return <div key={entry.name} className="flex items-center justify-between" data-id="element-5960" data-mp="2w4wq6oedm3gv">
                            <div className="flex items-center gap-2" data-id="element-5961" data-mp="363kw4ot36s47">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                        backgroundColor: entry.color
                      }} data-id="element-5962" data-mp="315b0ye7d3ril" />
                              <span className="text-sm text-white/60" data-id="element-5963" data-mp="2n5rtzoqaem7v">
                                {entry.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-3" data-id="element-5964" data-mp="1upqsj7bghd5d">
                              <span className="text-sm font-semibold text-white/90" data-id="element-5965" data-mp="1unw3phx2d0w0">
                                {Number(entry.value ?? 0).toLocaleString()}
                              </span>
                              <span className="text-xs text-white/30 w-10 text-right" data-id="element-5966" data-mp="57aezqsb3mhw">
                                {pct}%
                              </span>
                            </div>
                          </div>;
                })}
                    </div>
                  </>}
              </div>
            </div>

            {/* ─── Charts Row 2 ───────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5967" data-mp="sxxnad64we4z">
              {/* Hourly Sales */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5968" data-mp="eyxzt8ucy5ae">
                <div className="mb-6" data-id="element-5969" data-mp="jx99njknkfsm">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5970" data-mp="jw6kbg2zalet">
                    Ventas por Hora
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5971" data-mp="2zqqcut6daqiw">
                    Distribución a lo largo del día
                  </p>
                </div>
                {hourlyChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-5972" data-mp="3jz840g7n0o5d">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-5973" data-mp="3a83lx7mo0n6b">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5974" data-mp="365yu8efs20mb">
                      <BarChart data={hourlyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-5975" data-mp="363vwt9cap57k">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-5976" data-mp="3txns0abdbhan" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} data-id="element-5977" data-mp="2dhb5u9zqev4j" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-5978" data-mp="16rp97sxrn6nx" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-5980" data-mp="1ppt69p0dpf9d" />} data-id="element-5979" data-mp="1pp9ny76tgout" />
                        <Bar dataKey="gmv" name="GMV" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={32} opacity={0.8} data-id="element-5981" data-mp="1pnfafdxvjz0r" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Orders Daily */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5982" data-mp="17nvvb1a67kt0">
                <div className="mb-6" data-id="element-5983" data-mp="13kg0mahnvuiy">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5984" data-mp="13nlknbbfo67t">
                    Órdenes Diarias
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5985" data-mp="1cmp78p9t73r2">
                    Volumen de pedidos por día
                  </p>
                </div>
                {dailyChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-5986" data-mp="1mbgw2ojkp7xr">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-5987" data-mp="209fa1stzf2cq">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5988" data-mp="1vdq497rdcp7z">
                      <BarChart data={dailyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-5989" data-mp="2eyqc1fw9p4uh">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-5990" data-mp="29z41je7tq2pd" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} interval="preserveStartEnd" data-id="element-5991" data-mp="20yz0rmx4y2k6" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} data-id="element-5992" data-mp="1xqr07hliwwc8" />
                        <Tooltip content={<DarkTooltip isCurrency={false} data-id="element-5994" data-mp="1jpocl0evrdz7" />} data-id="element-5993" data-mp="1jr7sur0jdp0z" />
                        <Bar dataKey="orders" name="Órdenes" fill="#8b5cf6" radius={[3, 3, 0, 0]} maxBarSize={32} opacity={0.8} data-id="element-5995" data-mp="1er3dx5fa12cv" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>
            </div>

            {/* ─── Charts Row 3 (Horizontal Bars) ──────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5996" data-mp="r2tv8cdd0upy">
              {/* Top Restaurants */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5997" data-mp="32o2uiea785fs">
                <div className="mb-6" data-id="element-5998" data-mp="37nfmfdfxg2p8">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5999" data-mp="37me4qq4eohyj">
                    Top Restaurantes
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-6000" data-mp="3gonbd4wjzr6n">
                    Por GMV generado
                  </p>
                </div>
                {topRestaurantsChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-6001" data-mp="2nzyhnqxtuo4x">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-6002" data-mp="2sx87h7p4p1ta">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-6003" data-mp="2xqb11ftwob9h">
                      <BarChart data={topRestaurantsChartData} layout="vertical" margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0
                }} data-id="element-6004" data-mp="20anek2t08hnm">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} data-id="element-6005" data-mp="qab4nw5kqx11" />
                        <XAxis type="number" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-6006" data-mp="100w1be1otrhb" />
                        <YAxis dataKey="name" type="category" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.6)'
                  }} tickLine={false} axisLine={false} width={100} data-id="element-6007" data-mp="1iw2x2htc1nx0" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-6009" data-mp="33folkenx8trx" />} data-id="element-6008" data-mp="33g7z7j6jgw05" />
                        <Bar dataKey="gmv" name="GMV" fill="#f59e0b" radius={[0, 3, 3, 0]} barSize={20} opacity={0.8} data-id="element-6010" data-mp="33e510bv8fwkc" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Top Products */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-6011" data-mp="3h7cu67yyp045">
                <div className="mb-6" data-id="element-6012" data-mp="2aslnfnn5ql27">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-6013" data-mp="2apfxy7am9h5y">
                    Top Productos
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-6014" data-mp="xsqu2z3isni3">
                    Por ingresos generados
                  </p>
                </div>
                {topProductsChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-6015" data-mp="nvm7wz9ocs1a">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-6016" data-mp="e5b194ylw4qp">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-6017" data-mp="e489kutgly9q">
                      <BarChart data={topProductsChartData} layout="vertical" margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0
                }} data-id="element-6018" data-mp="ivrfuse5xwen">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} data-id="element-6019" data-mp="3rcl58b24mcxp" />
                        <XAxis type="number" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-6020" data-mp="3cr70jjjcdx6a" />
                        <YAxis dataKey="name" type="category" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.6)'
                  }} tickLine={false} axisLine={false} width={100} data-id="element-6021" data-mp="3rmd5s4am5ryk" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-6023" data-mp="e8jk32flrn1l" />} data-id="element-6022" data-mp="e80b4b7xk8zp" />
                        <Bar dataKey="revenue" name="Ingresos" fill="#ec4899" radius={[0, 3, 3, 0]} barSize={20} opacity={0.8} data-id="element-6024" data-mp="27q7f1ucp2dr6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>
            </div>

            {/* ─── Tables Section ─────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-6025" data-mp="1tpwnep3k8v4y">
              {/* Top Products Table */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] overflow-hidden flex flex-col" data-id="element-6026" data-mp="1yp9i1w0ob6i3">
                <div className="p-5 border-b border-white/[0.06]" data-id="element-6027" data-mp="23pdr50z94yya">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-6028" data-mp="3a57n7osqd8e1">
                    Detalle de Productos
                  </h2>
                </div>
                <div className="overflow-x-auto flex-1" data-id="element-6029" data-mp="3f1y4ugk6oc1k">
                  <table className="w-full text-sm" data-id="element-6030" data-mp="13gp6qhz2z7db">
                    <thead data-id="element-6031" data-mp="13i8ne9onfkfm">
                      <tr className="text-xs text-white/30 uppercase tracking-wider bg-white/[0.02]" data-id="element-6032" data-mp="ygt1kfend9w6">
                        <th className="text-left px-5 py-3 font-medium" data-id="element-6033" data-mp="tq2tuvir0uyb">
                          Producto
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-6034" data-mp="or00i8c4sd66">
                          Cant.
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-6035" data-mp="fqutw0erhit2">
                          Ingresos
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-6036" data-mp="atl7ktmo5n9g">
                      {data.topProducts.slice(0, 10).map((p, i) => <tr key={i} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-id="element-6037" data-mp="5v05sprrqviw" data-mpi={i}>
                          <td className="px-5 py-3" data-id="element-6038" data-mp="204vgtkeruryz">
                            <p className="font-medium text-white/90" data-id="element-6039" data-mp="3ulvhv5s5y2gx">
                              {p.product_name}
                            </p>
                            {restaurantId === 'all' && <p className="text-xs text-white/40 mt-0.5" data-id="element-6040" data-mp="3kq1vyvsk0wmh">
                                {p.restaurant_name}
                              </p>}
                          </td>
                          <td className="px-5 py-3 text-right text-white/60" data-id="element-6041" data-mp="h8s26jkn50yu">
                            {p.quantity}
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-emerald-400" data-id="element-6042" data-mp="7accml19hzrg">
                            {formatCurrency(p.revenue)}
                          </td>
                        </tr>)}
                      {data.topProducts.length === 0 && <tr data-id="element-6043" data-mp="2wywfqfipc2bn">
                          <td colSpan={3} className="px-5 py-8 text-center text-white/30" data-id="element-6044" data-mp="2wzxuatyxf6y4">
                            Sin datos
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recent Orders Table */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] overflow-hidden flex flex-col" data-id="element-6045" data-mp="1jyi60ep2bofm">
                <div className="p-5 border-b border-white/[0.06]" data-id="element-6046" data-mp="w8nrztt0btts">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-6047" data-mp="rdicbcq77be5">
                    Órdenes Recientes
                  </h2>
                </div>
                <div className="overflow-x-auto flex-1" data-id="element-6048" data-mp="10cvi9vf45hu4">
                  <table className="w-full text-sm" data-id="element-6049" data-mp="10bc1m3pjp4rt">
                    <thead data-id="element-6050" data-mp="cm9eigc56jpx">
                      <tr className="text-xs text-white/30 uppercase tracking-wider bg-white/[0.02]" data-id="element-6051" data-mp="clhrvv0ivwbs">
                        <th className="text-left px-5 py-3 font-medium" data-id="element-6052" data-mp="8kxuqrfy8spn">
                          ID / Cliente
                        </th>
                        <th className="text-left px-5 py-3 font-medium" data-id="element-6053" data-mp="12fbeme3ri3qo">
                          Estado
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-6054" data-mp="16fvdbm3zhfex">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-6055" data-mp="1gfw05i12adnv">
                      {data.recentOrders.slice(0, 10).map((o, i) => <tr key={o.id || i} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-id="element-6056" data-mp="1lde04ix3pijq" data-mpi={i}>
                          <td className="px-5 py-3" data-id="element-6057" data-mp="1u5fy8ajp6mqu">
                            <p className="font-medium text-white/90" data-id="element-6058" data-mp="1u8bw5yvjpbfa">
                              {String(o.id).substring(0, 8).toUpperCase()}
                            </p>
                            <p className="text-xs text-white/40 mt-0.5" data-id="element-6059" data-mp="244yf2i4b9seo">
                              {o.customer_name}
                            </p>
                          </td>
                          <td className="px-5 py-3" data-id="element-6060" data-mp="298r34nlqmon7">
                            <span className="px-2 py-1 rounded-md text-[10px] font-medium uppercase tracking-wider bg-white/[0.06] text-white/70" data-id="element-6061" data-mp="24a64gsm4wd0v">
                              {o.status}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-white" data-id="element-6062" data-mp="2wyljvg96gxcq">
                            {formatCurrency(o.total)}
                          </td>
                        </tr>)}
                      {data.recentOrders.length === 0 && <tr data-id="element-6063" data-mp="2j6x5t7fdbyt4">
                          <td colSpan={3} className="px-5 py-8 text-center text-white/30" data-id="element-6064" data-mp="2j5m217lxar20">
                            Sin datos
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Quick Links Section */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-6065" data-mp="qh338qtqqv1k">
              <Link to="/admin/enrollment/receipts" className="bg-[#12121a] border border-white/[0.06] rounded-2xl p-6 hover:bg-white/[0.02] transition-colors group flex items-center justify-between" data-id="element-6066" data-mp="lhgoue26f8r6">
                <div className="flex items-center gap-4" data-id="element-6067" data-mp="uhdjcwb8z1u5">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-6068" data-mp="196rqryv9j96w">
                    <FileTextIcon className="w-6 h-6 text-emerald-400" data-id="element-6069" data-mp="148gi3rgpf4q9" />
                  </div>
                  <div data-id="element-6070" data-mp="1i893r8ddiurd">
                    <h3 className="text-white font-bold" data-id="element-6071" data-mp="1i5d6znd9ic4i">Recibos de Pago</h3>
                    <p className="text-sm text-white/50 mt-0.5" data-id="element-6072" data-mp="1e7p5u2kz7n46">
                      Generar y consultar recibos
                    </p>
                  </div>
                </div>
                <ArrowLeftIcon className="w-5 h-5 text-white/30 rotate-180 group-hover:text-white/70 transition-colors" data-id="element-6073" data-mp="21s81nfczstxq" />
              </Link>
            </div>
          </>}
      </main>
    </div>;
}